import React, { useState, useEffect } from 'react';
import { sendLead } from '../../config/api';

// Função para pegar UTMs da URL
function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  const utms = [];
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
    if (params.get(key)) {
      utms.push(`${key}=${params.get(key)}`);
    }
  });
  return utms.length ? '&' + utms.join('&') : '';
}

// Função para formatar telefone brasileiro
function formatPhone(value) {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
  if (digits.length <= 11) return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7,11)}`;
  return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7,11)}`;
}

const ContactModal = ({ isOpen, onClose, onSubmit, message = "Falta pouco para você descobrir o valor real do seu console" }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState('');

  // Animação de entrada
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    if (isOpen && window.fbq) {
      window.fbq('trackCustom', 'OpenContactModal');
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    }
    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    } else if (!/^\d{10,15}$/.test(formData.telefone.replace(/\D/g, ''))) {
      newErrors.telefone = 'Telefone inválido';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'telefone' ? formatPhone(value) : value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const dataToSend = {
        nome: formData.nome.trim(),
        telefone: formData.telefone.replace(/\D/g, ''),
        email: 'nao_informado@gamav2.com', // valor mock para o n8n
        timestamp: new Date().toISOString()
      };
      // Pixel: Lead e GoToWhatsAppAfterForm
      if (window.fbq) {
        window.fbq('track', 'Lead');
        window.fbq('trackCustom', 'GoToWhatsAppAfterForm');
      }
      // Enviar para n8n
      await sendLead(dataToSend);
      // WhatsApp
      const utms = getUtmParams();
      const msg = encodeURIComponent(`Olá! Meu nome é ${formData.nome}, meu telefone é ${formData.telefone}. Quero saber o valor do meu console!`);
      const link = `https://api.whatsapp.com/send/?phone=554799089366&text=${msg}${utms}`;
      window.open(link, '_blank');
      handleClose();
    } catch (e) {
      alert('Erro ao enviar seus dados. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ nome: '', telefone: '' });
    setErrors({});
    setIsSubmitting(false);
    setCurrentStep(1);
    setFocusedField('');
    onClose();
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setFocusedField('');
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setFocusedField('');
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  // NOVO: Apenas botão WhatsApp, formulário comentado para teste
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden transform transition-all duration-300 animate-slideUp">
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#22c55e] to-[#16a34a] rounded-full mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {message}
            </h2>
            <p className="text-gray-600 text-sm">
              Clique no botão abaixo para falar diretamente no WhatsApp e receber sua avaliação personalizada.
            </p>
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100"
              disabled={isSubmitting}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* FORMULÁRIO ORIGINAL COMENTADO PARA TESTE */}
          {false && (
          <form onSubmit={handleSubmit} className="w-full space-y-6">
            <div className="space-y-4 animate-fadeInStep">
              <div className="relative">
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                  Seu nome completo
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  onFocus={() => handleFocus('nome')}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-4 border-2 rounded-xl text-lg text-gray-900 placeholder-gray-500 transition-all duration-300 ${
                    errors.nome 
                      ? 'border-red-400 bg-red-50' 
                      : focusedField === 'nome'
                        ? 'border-[#22c55e] bg-green-50 shadow-lg transform scale-[1.02]'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  placeholder="Ex: João Silva"
                  disabled={isSubmitting}
                  autoFocus
                />
                {errors.nome && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {errors.nome}
                  </p>
                )}
              </div>
              <div className="relative">
                <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                  Seu número de WhatsApp
                </label>
                <input
                  type="tel"
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  onFocus={() => handleFocus('telefone')}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-4 border-2 rounded-xl text-lg text-gray-900 placeholder-gray-500 transition-all duration-300 ${
                    errors.telefone 
                      ? 'border-red-400 bg-red-50' 
                      : focusedField === 'telefone'
                        ? 'border-[#22c55e] bg-green-50 shadow-lg transform scale-[1.02]'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  placeholder="Ex: (47) 99999-9999"
                  disabled={isSubmitting}
                  inputMode="tel"
                  pattern="[0-9()\-\s]{14,15}"
                  maxLength={15}
                />
                {errors.telefone && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {errors.telefone}
                  </p>
                )}
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 md:py-5 bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white rounded-xl font-semibold text-lg md:text-xl shadow-cta hover:scale-[1.01] active:scale-95 transition-all duration-300 touch-manipulation"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar e ir para WhatsApp'}
            </button>
          </form>
          )}
          {/* FIM FORMULÁRIO ORIGINAL */}
          <a
            href="https://api.whatsapp.com/send/?phone=554799089366"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full block py-4 md:py-5 bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white rounded-xl font-semibold text-lg md:text-xl shadow-cta hover:scale-[1.01] active:scale-95 transition-all duration-300 text-center mt-4"
          >
            <i className="fab fa-whatsapp mr-2"></i> Falar direto no WhatsApp
          </a>
        </div>
      </div>
      <style jsx>{`
        @media (max-width: 640px) {
          .bg-white.rounded-2xl.shadow-2xl.max-w-lg.w-full.mx-4 {
            max-width: 98vw !important;
            min-width: 0;
            margin: 0 auto;
            border-radius: 18px;
            padding: 0 !important;
            max-height: 98vh !important;
          }
          .p-8 {
            padding: 1.25rem !important;
          }
          .text-center.mb-8 {
            margin-bottom: 1.5rem !important;
          }
          .inline-flex.items-center.justify-center.w-16.h-16 {
            width: 48px !important;
            height: 48px !important;
            margin-bottom: 0.5rem !important;
          }
          .text-2xl.font-bold.text-gray-900.mb-2 {
            font-size: 1.1rem !important;
            margin-bottom: 0.5rem !important;
          }
          .min-h-[350px] {
            min-height: 180px !important;
          }
          .space-y-4.animate-fadeInStep {
            gap: 0.75rem !important;
          }
          .w-full.px-4.py-4.border-2.rounded-xl.text-lg.text-gray-900.placeholder-gray-500 {
            padding: 0.75rem 1rem !important;
            font-size: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactModal; 